/**
 * Componente FilterBar - Barra de Filtros Reutilizable
 * Diseño destacado con contador de resultados y filtros activos
 * ESTÁNDAR: Usar en toda la aplicación para barras de filtros
 */

import React from 'react';
import {
  Box,
  Paper,
  TextField,
  InputAdornment,
  useTheme,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Search, Clear, FilterList } from '@mui/icons-material';
import { CorporateBadge } from './CorporateBadge';
import { SxProps, Theme } from '@mui/material/styles';

interface Filter {
  key: string;
  label: string;
  value: string;
}

interface FilterBarProps {
  /**
   * Valor del campo de búsqueda
   */
  searchValue: string;
  
  /**
   * Función que se ejecuta al cambiar el valor de búsqueda
   */
  onSearchChange: (value: string) => void;
  
  /**
   * Placeholder del campo de búsqueda
   */
  searchPlaceholder?: string;
  
  /**
   * Número de resultados filtrados
   */
  resultCount?: number;
  
  /**
   * Total de resultados sin filtrar
   */
  totalCount?: number;
  
  /**
   * Filtros activos a mostrar como chips
   */
  activeFilters?: Filter[];
  
  /**
   * Función para eliminar un filtro específico
   */
  onRemoveFilter?: (key: string) => void;
  
  /**
   * Función para limpiar todos los filtros
   */
  onClearAllFilters?: () => void;
  
  /**
   * Elementos adicionales a mostrar en la barra
   */
  additionalActions?: React.ReactNode;
  
  /**
   * Estilos personalizados
   */
  sx?: SxProps<Theme>;
}

export const FilterBar: React.FC<FilterBarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Buscar...',
  resultCount,
  totalCount,
  activeFilters = [],
  onRemoveFilter,
  onClearAllFilters,
  additionalActions,
  sx,
}) => {
  const theme = useTheme();
  const hasActiveFilters = activeFilters.length > 0;
  const showCount = resultCount !== undefined && totalCount !== undefined;
  
  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        backgroundColor: theme.custom.colors.neutral.light,
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme.palette.divider,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
          <FilterList color="primary" />
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchValue && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onSearchChange('')}
                    edge="end"
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {showCount && (
          <CorporateBadge
            variant="custom"
            type="custom-info"
            label={`${resultCount} de ${totalCount} resultados`}
            showIcon={false}
            gradient={false}
            shadow={true}
          />
        )}
        
        {hasActiveFilters && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {activeFilters.map((filter) => (
              <Chip
                key={filter.key}
                label={`${filter.label}: ${filter.value}`}
                size="small"
                onDelete={onRemoveFilter ? () => onRemoveFilter(filter.key) : undefined}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              />
            ))}
            {onClearAllFilters && (
              <Tooltip title="Limpiar todos los filtros" arrow>
                <IconButton
                  size="small"
                  onClick={onClearAllFilters}
                  sx={{ ml: 1 }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        
        {additionalActions && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {additionalActions}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

